import type {
  CatalogEvent,
  CatalogField,
  EventCatalog,
  Family,
  FieldType,
  MomentClass,
} from './schemas';

export const VALUE_SET_KEYS = [
  'formatType',
  'formatSubtype',
  'location',
] as const;

export const VALUE_SETS: Record<(typeof VALUE_SET_KEYS)[number], string[]> = {
  formatType: ['dexp-offering', 'dexp-ad', 'affiliate'],
  formatSubtype: [
    'dexp-results',
    'dexp-unqualified',
    'dexp-editorial-listings',
    'credible-student-loans',
    'dexp-multi-question',
    'dexp-banner-cta',
    'dexp-unknown',
  ],
  location: [
    'dexp-embedded-recommender',
    'dexp-pop-up-recommender',
    'dexp-results-page',
    'dexp-undergrad-thank-you-page',
    'dexp-recommender-grad-thank-you-page',
    'dexp-ready-to-go-thank-you-page',
    'dexp-thank-you-page',
    'dexp-spotlight-popup',
    'dexp-conversion-drawer',
    'dexp-results-page-pop-up',
    'dexp-comparison-modal',
    'dexp-comparison-popup',
  ],
};

export const RESERVED_CUSTOM_DIMENSION_KEYS = [
  'dslConfigId',
  'dslConfigVersion',
  'blockId',
  'eventBindingId',
  'mappingFallback',
  'sourceCorrelationId',
  'resultsSource',
] as const;

const SCHEMA_VERSION_SOURCE = String.raw`\.v(\d+)(?![A-Za-z0-9])`;

/** Last `.vN` token in a Make schema name, including a version that is not the final segment. */
export function parseSchemaVersion(schema: string): string | undefined {
  const pattern = new RegExp(SCHEMA_VERSION_SOURCE, 'g');
  let last: RegExpExecArray | undefined;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(schema)) !== null) {
    last = match;
  }
  return last ? `v${last[1]}` : undefined;
}

function text(path: string): [string, CatalogField] {
  return [path, { type: 'string' }];
}

function governed(
  valueSet: 'formatType' | 'formatSubtype' | 'location',
): CatalogField {
  return {
    type: 'string',
    rules: { valueSet, mode: 'governed', blocking: false },
  };
}

const platformId: CatalogField = { type: 'string', rules: { platform: true } };

function paths(
  type: FieldType,
  names: readonly string[],
): Record<string, CatalogField> {
  return Object.fromEntries(
    names.map((name) => [name, { type, rules: { platform: true } }]),
  );
}

const webContextFields: Record<string, CatalogField> = {
  ...paths('string', [
    'webContext.anonymousId',
    'webContext.authenticatedUserId',
    'webContext.instanceId',
    'webContext.ip',
    'webContext.manualInstanceId',
    'webContext.sessionId',
    'webContext.userAgent',
    'webContext.crossSiteId',
    'webContext.hashId',
    'webContext.sender',
    'webContext.pccId',
    'webContext.library.name',
    'webContext.library.version',
    'webContext.page.referrer',
    'webContext.page.title',
    'webContext.page.url',
    'webContext.page.revisionId',
    'webContext.page.pageType',
    'webContext.page.postId',
  ]),
  ...paths('boolean', [
    'webContext.fromManual',
    'webContext.cohesionConsent',
    'webContext.cookieConsent.essential',
    'webContext.cookieConsent.performance',
    'webContext.cookieConsent.preference',
    'webContext.cookieConsent.targeting',
    'webContext.cookieConsent.social',
  ]),
  ...paths('number', ['webContext.timezoneOffset']),
};

const webElementFields = Object.fromEntries([
  text('webElement.elementType'),
  text('webElement.htmlId'),
  text('webElement.location'),
  text('webElement.name'),
  text('webElement.position'),
  text('webElement.text'),
]);

const elementFields: Record<string, CatalogField> = {
  ...webContextFields,
  ...webElementFields,
  correlationId: platformId,
};

const elementWithVariant: Record<string, CatalogField> = {
  ...elementFields,
  'webElement.variant': { type: 'string' },
  viewCorrelationId: platformId,
};

const elementClickedFields: Record<string, CatalogField> = {
  ...elementWithVariant,
  actionOutcome: { type: 'string' },
  outboundUrl: { type: 'string' },
};

const productFields: Record<string, CatalogField> = {
  ...Object.fromEntries([
    text('product.brand'),
    text('product.category'),
    text('product.coupon'),
    text('product.name'),
    text('product.pageId'),
    text('product.sku'),
    text('product.text'),
    text('product.upc'),
    text('product.variant'),
    text('product.positionEngine'),
    text('product.productId'),
    text('product.subscription.frequency'),
  ]),
  'product.formatType': governed('formatType'),
  'product.formatSubtype': governed('formatSubtype'),
  'product.location': governed('location'),
  'product.position': { type: 'number' },
  'product.quantity': { type: 'number' },
  'product.price': { type: 'number' },
};

const productBaseFields: Record<string, CatalogField> = {
  ...webContextFields,
  ...productFields,
  listId: { type: 'string' },
  viewCorrelationId: platformId,
};

const productClickedFields: Record<string, CatalogField> = {
  ...productBaseFields,
  actionOutcome: { type: 'string' },
  correlationId: platformId,
  outboundUrl: { type: 'string' },
};

function event(
  schema: string,
  props: {
    family?: Family;
    authorable: boolean;
    fits: MomentClass[];
    acceptsCustomDimensions: boolean;
    fields?: Record<string, CatalogField>;
  },
): CatalogEvent {
  return {
    schema,
    schemaVersion: parseSchemaVersion(schema) ?? '',
    ...props,
  };
}

/** Catalog events in the documented table order. */
const events: Record<string, CatalogEvent> = {
  ElementLoaded: event('core.ElementLoaded.v1', {
    family: 'element',
    authorable: true,
    fits: ['mount'],
    acceptsCustomDimensions: false,
    fields: elementFields,
  }),
  ElementViewed: event('redventures.usertracking.v3.ElementViewed', {
    family: 'element',
    authorable: true,
    fits: ['view'],
    acceptsCustomDimensions: true,
    fields: elementWithVariant,
  }),
  ElementClicked: event('redventures.usertracking.v3.ElementClicked', {
    family: 'element',
    authorable: true,
    fits: ['click'],
    acceptsCustomDimensions: true,
    fields: elementClickedFields,
  }),
  ProductLoaded: event('redventures.ecommerce.v1.ProductLoaded', {
    family: 'product',
    authorable: true,
    fits: ['mount'],
    acceptsCustomDimensions: true,
    fields: productBaseFields,
  }),
  ProductViewed: event('redventures.ecommerce.v1.ProductViewed', {
    family: 'product',
    authorable: true,
    fits: ['view'],
    acceptsCustomDimensions: true,
    fields: productBaseFields,
  }),
  ProductClicked: event('redventures.ecommerce.v1.ProductClicked', {
    family: 'product',
    authorable: true,
    fits: ['click'],
    acceptsCustomDimensions: true,
    fields: productClickedFields,
  }),
  FieldViewed: event('core.fieldViewed.v1', {
    family: 'field',
    authorable: false,
    fits: ['view'],
    acceptsCustomDimensions: false,
  }),
  FieldSelected: event('redventures.usertracking.v3.FieldSelected', {
    family: 'field',
    authorable: false,
    fits: ['change'],
    acceptsCustomDimensions: false,
  }),
  FieldInputted: event('redventures.usertracking.v3.FieldInputted', {
    family: 'field',
    authorable: false,
    fits: ['change'],
    acceptsCustomDimensions: false,
  }),
  FormViewed: event('redventures.usertracking.v3.FormViewed', {
    family: 'form',
    authorable: false,
    fits: ['view'],
    acceptsCustomDimensions: false,
  }),
  FormStarted: event('redventures.usertracking.v3.FormStarted', {
    family: 'form',
    authorable: false,
    fits: [],
    acceptsCustomDimensions: false,
  }),
  FormContinued: event('redventures.usertracking.v3.FormContinued', {
    family: 'form',
    authorable: false,
    fits: [],
    acceptsCustomDimensions: false,
  }),
  FormSubmitted: event('redventures.usertracking.v3.FormSubmitted', {
    family: 'form',
    authorable: false,
    fits: ['submit'],
    acceptsCustomDimensions: false,
  }),
  ConversionTracked: event('core.conversions.ConversionTracked.v2', {
    authorable: false,
    fits: [],
    acceptsCustomDimensions: false,
  }),
  ServiceRequestTracked: event('highereducation.ServiceRequestTracked.v2', {
    authorable: false,
    fits: [],
    acceptsCustomDimensions: false,
  }),
  ServiceResponseTracked: event('highereducation.ServiceResponseTracked.v2', {
    authorable: false,
    fits: [],
    acceptsCustomDimensions: false,
  }),
  Identify: event('core.Identify.v1', {
    family: 'identity',
    authorable: false,
    fits: [],
    acceptsCustomDimensions: false,
  }),
};

export const eventCatalog = {
  catalogVersion: 1,
  valueSets: {
    formatType: { values: VALUE_SETS.formatType },
    formatSubtype: { values: VALUE_SETS.formatSubtype },
    location: { values: VALUE_SETS.location },
  },
  reservedCustomDimensionKeys: [...RESERVED_CUSTOM_DIMENSION_KEYS],
  events,
} satisfies EventCatalog;
